import React, { useCallback } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { IoMdNotificationsOutline } from 'react-icons/io';
import { api } from '@/utils/Api/POS';
import type { GetApiDoctorsPortalNotifications200NotificationsItem } from '@/shared/api/models';
import { RadixButton, RadixSpinner } from '@/components/radix';
import { cnMerge } from '@/utils/cnMerge';
import { formatDate } from '@/utils/dateFormatter';

const INVITATION_ENTITY = 'INVITATION';

function isInvitationEntity(
    notification: GetApiDoctorsPortalNotifications200NotificationsItem,
): boolean {
    return (notification.notificationEntityType ?? '').toString().toUpperCase() === INVITATION_ENTITY;
}

export default function DoctorsNotificationsBell() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [open, setOpen] = React.useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['doctors-portal-notifications'],
        queryFn: () =>
            api.getApiDoctorsPortalNotifications({
                page: 1,
                limit: 20,
            }),
        staleTime: 60 * 1000,
    });

    const markReadMutation = useMutation({
        mutationFn: (body: { notificationIds?: string[]; readAll?: boolean }) =>
            api.patchApiDoctorsPortalNotificationsRead(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['doctors-portal-notifications'] });
        },
    });

    const notifications = data?.notifications ?? [];
    const unreadCount = notifications.filter((notification) => !notification.readAt).length;

    const handleNotificationClick = useCallback(
        (notification: GetApiDoctorsPortalNotifications200NotificationsItem) => {
            const id = notification.notificationId;
            const doctorUuid =
                notification.doctorUuid != null && notification.doctorUuid !== ''
                    ? String(notification.doctorUuid)
                    : undefined;
            const isInvitation = isInvitationEntity(notification);

            if (id) {
                markReadMutation.mutate({ notificationIds: [id] });
            }

            setOpen(false);
            queueMicrotask(() => {
                if (isInvitation) {
                    navigate('/doctors');
                } else {
                    const state = doctorUuid ? { doctorUuid } : undefined;
                    navigate('/doctors/messages', { state });
                }
            });
        },
        [navigate, markReadMutation],
    );

    const handleMarkAllRead = useCallback(() => {
        markReadMutation.mutate({ readAll: true });
    }, [markReadMutation]);

    const contentClasses = cnMerge(
        'min-w-[280px] max-w-[360px] max-h-[70vh] overflow-hidden',
        'bg-white rounded-md shadow-lg border border-border-default z-[9999]',
        'dark:bg-background-paper dark:border-border-default',
        'flex flex-col',
    );

    return (
        <DropdownMenu.Root open={open} onOpenChange={setOpen}>
            <DropdownMenu.Trigger asChild>
                <RadixButton variant="ghost" size="xs" iconOnly className={cnMerge('relative !rounded-full !p-4')}>
                    <IoMdNotificationsOutline className="w-5 h-5 shrink-0" />
                    {unreadCount > 0 && (
                        <span
                            className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-white text-xs font-medium"
                            aria-hidden
                        >
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </RadixButton>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content className={contentClasses} align="end" side="bottom" sideOffset={6}>
                    <div className="flex items-center justify-between px-3 py-2 border-0 border-b-[1px] border-solid border-border-default">
                        <span className="font-semibold text-lg text-text-primary">{t('Calendar.Notifications')}</span>
                        {unreadCount > 0 && (
                            <RadixButton
                                variant="ghost"
                                size="sm"
                                className="text-xs border-none text-primary-500 underline hover:bg-transparent hover:text-primary-600"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleMarkAllRead();
                                }}
                            >
                                {t('Calendar.MarkRead')}
                            </RadixButton>
                        )}
                    </div>

                    <div className="overflow-y-auto p-1">
                        {isLoading ? (
                            <div className="flex justify-center py-8">
                                <RadixSpinner className="w-8 h-8" />
                            </div>
                        ) : notifications.length === 0 ? (
                            <p className="py-6 px-3 text-sm text-text-secondary text-center">
                                {t('Doctors.NoNotifications')}
                            </p>
                        ) : (
                            notifications.map((notification) => {
                                const unread = !notification.readAt;
                                return (
                                    <DropdownMenu.Item
                                        key={notification.notificationId}
                                        className={cnMerge(
                                            'flex flex-col gap-0.5 px-3 py-2.5 rounded-sm cursor-pointer',
                                            'text-left text-sm outline-none',
                                            'hover:bg-grey-100 dark:hover:bg-grey-800',
                                            unread && 'bg-grey-50 dark:bg-grey-900/30',
                                        )}
                                        onSelect={(e) => {
                                            e.preventDefault();
                                            handleNotificationClick(notification);
                                        }}
                                    >
                                        <span className="flex items-center gap-1 font-medium text-text-primary">
                                            {unread && <span className="w-2 h-2 rounded-full bg-primary-500 " />}
                                            <p className="text-text-primary line-clamp-1 m-0 p-0">
                                                {notification?.actionData}
                                            </p>
                                        </span>
                                        <span className="text-xs text-text-secondary">
                                            {formatDate(notification.createdAt, 'DD/MM-YYYY HH:mm')}
                                        </span>
                                    </DropdownMenu.Item>
                                );
                            })
                        )}
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
